import { z } from "zod";
import { IsoDate, IsoDateTime, Uuid } from "./common.js";

// ---- Chat entre sucursales (ADR 0028, migration 0029) -----------------------------------
//
// One message board per laboratory, text only, for its staff: author, branch and time. Messages are
// lab records in the audit chain and are never deleted; the author may hide one ("ocultar"), which
// keeps the row and shows "Mensaje ocultado" in its place. Unread state is one last-read marker per
// user (server time), so the bubble's badge is the same on every device of that user.

export const CHAT_MESSAGE_MAX = 1000;

export const ChatMessage = z.object({
  id: Uuid,
  /** null once hidden by its author. */
  body: z.string().nullable(),
  hidden: z.boolean(),
  author_id: Uuid,
  author_name: z.string(),
  branch_id: Uuid,
  branch_name: z.string(),
  /** Server time it was stored: the order of the board and the unread marker. */
  created_at: IsoDateTime,
  /** Written by the signed-in user. */
  mine: z.boolean(),
});
export type ChatMessage = z.infer<typeof ChatMessage>;

/** GET /v1/chat — the latest messages, oldest first; `before` pages back. */
export const ChatQuery = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  before: IsoDateTime.optional(),
});
export type ChatQuery = z.infer<typeof ChatQuery>;

export const ChatView = z.object({
  items: z.array(ChatMessage),
  /** Messages by others, not hidden, after the user's last-read marker. */
  unread: z.number().int().min(0),
  last_read_at: IsoDateTime.nullable(),
  /** More, older messages exist before the first item. */
  has_more: z.boolean(),
});
export type ChatView = z.infer<typeof ChatView>;

/** GET /v1/chat/unread — the bubble's badge, one cheap query. */
export const ChatUnread = z.object({ count: z.number().int().min(0), latest_at: IsoDateTime.nullable() });
export type ChatUnread = z.infer<typeof ChatUnread>;

export const ChatSendPayload = z.object({
  body: z.string().trim().min(1, "Escribe el mensaje.").max(CHAT_MESSAGE_MAX),
});
export type ChatSendPayload = z.infer<typeof ChatSendPayload>;

export const ChatHidePayload = z.object({ message_id: Uuid });
export type ChatHidePayload = z.infer<typeof ChatHidePayload>;

/** Everything up to `up_to` (a message's `created_at`) is read. The marker never moves back. */
export const ChatReadPayload = z.object({ up_to: IsoDateTime });
export type ChatReadPayload = z.infer<typeof ChatReadPayload>;

// ---- Inicio -----------------------------------------------------------------------------

/** GET /v1/home — today's numbers at a branch (orders registered today, lab-local). */
export const HomeQuery = z.object({ branch_id: Uuid });
export type HomeQuery = z.infer<typeof HomeQuery>;

export const HomeSummary = z.object({
  today: IsoDate,
  /** Orders registered today at the branch (cancelled excluded). */
  orders: z.number().int().min(0),
  /** Their live studies, by state. */
  studies: z.number().int().min(0),
  to_capture: z.number().int().min(0),
  to_validate: z.number().int().min(0),
  validated: z.number().int().min(0),
  delivered: z.number().int().min(0),
  /** Tubes still to collect at the branch, any day (the Muestras pendientes badge). */
  pending_samples: z.number().int().min(0),
});
export type HomeSummary = z.infer<typeof HomeSummary>;
